/**
 * Navigation Adapter
 *
 * Provides framework-agnostic navigation utilities that work across
 * different runtime environments.
 */

export interface NavigationAdapter {
  push: (path: string) => void;
  replace: (path: string) => void;
  back: () => void;
  pathname: string;
  searchParams: URLSearchParams;
}

/**
 * Creates a navigation adapter based on the current runtime environment
 */
export const createNavigation = (): NavigationAdapter => {
  // Server-side / Node.js environment - return mock
  if (typeof window === "undefined") {
    return {
      push: (path: string) => console.warn("Navigation not available in this environment:", path),
      replace: (path: string) =>
        console.warn("Navigation not available in this environment:", path),
      back: () => console.warn("Navigation not available in this environment"),
      pathname: "/",
      searchParams: new URLSearchParams(),
    };
  }

  // Browser environment - use window.history
  const location = window.location;
  return {
    push: (path: string) => {
      window.history.pushState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    replace: (path: string) => {
      window.history.replaceState({}, "", path);
      window.dispatchEvent(new PopStateEvent("popstate"));
    },
    back: () => window.history.back(),
    pathname: location.pathname,
    searchParams: new URLSearchParams(location.search),
  };
};

// Singleton instance for stable reference across renders (created lazily for SSR compatibility)
let navigationInstance: NavigationAdapter | null = null;

const getNavigationInstance = (): NavigationAdapter => {
  if (!navigationInstance) {
    navigationInstance = createNavigation();
  }
  return navigationInstance;
};

/**
 * Hook-like interface for navigation (React-compatible)
 * Returns a stable reference to avoid useEffect dependency issues
 */
export const useNavigation = () => {
  return getNavigationInstance();
};
